import React, { useRef, useEffect, useState } from 'react';

const ScratchCard = ({ children, width = 300, height = 80, coverColor = '#C0C0C0', onReveal }) => {
    const canvasRef = useRef(null);
    const containerRef = useRef(null);
    const [isDrawing, setIsDrawing] = useState(false);
    const [isRevealed, setIsRevealed] = useState(false);

    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const container = containerRef.current;

        // Set canvas size to match container
        if (container) {
            canvas.width = container.offsetWidth;
            canvas.height = container.offsetHeight;
        }

        // Fill with cover color
        ctx.fillStyle = coverColor;
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Add "Scratch Here" text
        ctx.fillStyle = '#666';
        ctx.font = 'bold 16px sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('SCRATCH TO REVEAL', canvas.width / 2, canvas.height / 2);

    }, [coverColor]);

    const getMousePos = (e) => {
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        return {
            x: (e.clientX || e.touches[0].clientX) - rect.left,
            y: (e.clientY || e.touches[0].clientY) - rect.top
        };
    };

    const scratch = (x, y) => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');

        ctx.globalCompositeOperation = 'destination-out';
        ctx.beginPath();
        ctx.arc(x, y, 20, 0, 2 * Math.PI);
        ctx.fill();
    };

    const handleMouseDown = (e) => {
        setIsDrawing(true);
        const { x, y } = getMousePos(e);
        scratch(x, y);
    };

    const handleMouseMove = (e) => {
        if (!isDrawing) return;
        const { x, y } = getMousePos(e);
        scratch(x, y);
        checkReveal();
    };

    const handleMouseUp = () => {
        setIsDrawing(false);
    };

    const checkReveal = () => {
        if (isRevealed) return;

        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        // Sample pixels to check transparency
        // Optimization: check every 10th pixel to save performance
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;
        let transparentPixels = 0;
        const totalPixels = data.length / 4;

        for (let i = 0; i < data.length; i += 4 * 10) {
            if (data[i + 3] === 0) {
                transparentPixels++;
            }
        }

        // If > 40% is cleared, reveal all
        if (transparentPixels / (totalPixels / 10) > 0.4) {
            setIsRevealed(true);
            if (onReveal) onReveal();
            canvas.style.transition = 'opacity 0.5s ease';
            canvas.style.opacity = '0';
            setTimeout(() => {
                canvas.style.display = 'none';
            }, 500);
        }
    };

    return (
        <div
            ref={containerRef}
            style={{
                position: 'relative',
                width: '100%',
                height: '100%',
                userSelect: 'none',
                overflow: 'hidden',
                borderRadius: '8px'
            }}
        >
            {/* Content to be revealed */}
            <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {children}
            </div>

            {/* Scratch Canvas */}
            <canvas
                ref={canvasRef}
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    touchAction: 'none',
                    cursor: 'pointer',
                    zIndex: 10
                }}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                onTouchStart={handleMouseDown}
                onTouchMove={handleMouseMove}
                onTouchEnd={handleMouseUp}
            />
        </div>
    );
};

export default ScratchCard;
